// =====================================================
// COMPOSANT: KpiCard — carte indicateur global avec delta N-1
// =====================================================
// Design system crm-4l (Card + tokens zinc). Étend StatisticCard avec le delta
// N-1 (valeur + Δ absolu + Δ% N-1) attendu par le dashboard stats. Vert/rouge =
// conventions sémantiques du CRM (green-*/red-* comme HomePage).

import type { LucideIcon } from 'lucide-react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import type { KpiValue } from '@/lib/dashboard-stats';

interface KpiCardProps {
  label: string;
  description: string;
  icon: LucideIcon;
  kpi: KpiValue;
  showN1: boolean;
  active?: boolean;
  onClick?: () => void;
}

function fmt(n: number): string {
  return n.toLocaleString('fr-FR');
}

/** Formatte le Δ% : Infinity → « +100% », null → « N/A ». */
function fmtPct(pct: number | null): string {
  if (pct == null) return 'N/A';
  if (!isFinite(pct)) return '+100%';
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(1)}%`;
}

export function KpiCard({
  label,
  description,
  icon: Icon,
  kpi,
  showN1,
  active = false,
  onClick,
}: KpiCardProps) {
  const isClickable = !!onClick;
  const positive = (kpi.deltaAbs ?? 0) >= 0;
  const DeltaIcon = kpi.n1Unavailable ? Minus : positive ? TrendingUp : TrendingDown;

  return (
    <Card
      className={[
        'border transition-colors',
        active ? 'border-zinc-900 ring-1 ring-zinc-900' : 'border-zinc-200',
        isClickable ? 'cursor-pointer hover:border-zinc-300 hover:bg-zinc-50' : '',
      ].join(' ')}
      onClick={onClick}
    >
      <CardContent className="px-6">
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-zinc-950">{label}</h3>
            <Icon className="h-4 w-4 text-zinc-900" />
          </div>

          <div className="flex flex-col gap-1">
            <p className="text-2xl font-bold text-zinc-950 tabular-nums">{fmt(kpi.value)}</p>

            {showN1 ? (
              kpi.n1Unavailable ? (
                <span className="inline-flex w-fit items-center gap-1 rounded-full border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-xs font-medium text-zinc-500">
                  <Minus className="h-3 w-3" />
                  N/A vs N-1
                </span>
              ) : (
                <span
                  className={[
                    'inline-flex w-fit items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold tabular-nums',
                    positive
                      ? 'border-green-200 bg-green-50 text-green-700'
                      : 'border-red-200 bg-red-50 text-red-700',
                  ].join(' ')}
                >
                  <DeltaIcon className="h-3 w-3" />
                  {positive ? '+' : ''}
                  {fmt(kpi.deltaAbs ?? 0)}
                  <span className="font-normal text-zinc-500">({fmtPct(kpi.deltaPct)} N-1)</span>
                </span>
              )
            ) : null}

            <p className="text-xs text-zinc-500">{description}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
